/* eslint-disable jsx-a11y/anchor-is-valid */
import { usePrevious } from '@dnd-kit/utilities';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { OverridableStringUnion } from '@mui/types';
import { useMutation } from '@tanstack/react-query';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { saveAdGroup } from '../../api/ventura/adGroup';
import { API_AD_GROUP_STATUS_URL } from '../../constants/apiUrls';
import { apiHostMap } from '../../constants/config';
import { ADMIN_CONTRACT_REVIEW_URL, EDIT_AD_GROUP_URL, EDIT_LINE_ITEM_URL } from '../../constants/routeUrls';
import useAdGroups from '../../hooks/useAdGroups';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useConstants } from '../../hooks/useConstants';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import useModal from '../../hooks/useModal';
import usePauseToggleAdGroup from '../../hooks/usePauseToggleAdGroup';
import { useSnackBar } from '../../hooks/useSnackBar';
import useVersions, { ModelType } from '../../hooks/useVersions';
import { Link } from '../../inmarkit';
import mapAdGroupToForm from '../../util/mapAdGroupToForm';
import mapFormToAdGroup from '../../util/mapFormToAdGroup';
import AdGroupSchema from '../../validationSchemas/AdGroupSchema';
import ContractConfigurationBreadcrumb from '../Contracts/ContractConfigurationBreadcrumb';
import ContractReportsPageLink from '../Contracts/ContractReportsPageLink';
import FormEndOptions from '../forms/FormEndOptions';
import LookerLink from '../shared/LookerLink';
import AdGroupCreativesSection from './AdGroupCreativesSection';
import AdGroupGeneralSection from './AdGroupGeneralSection';
import AdGroupLocationsSection from './AdGroupLocationsSection';
import AdGroupProductsSection from './AdGroupProductsSection';
import AdGroupTabs from './AdGroupTabs';
import AdGroupTargetingSection from './AdGroupTargetingSection';
import CampaignReadinessPanel from './CampaignReadinessPanel';
import DuplicateAdGroupForm from './DuplicateAdGroupForm';

interface ButtonPropsColorOverrides {}

interface AdGroupFormProps {
  adGroup: any;
  contract?: any;
  activeTab?: string;
  refetch?: Function;
}

function AdGroupForm({ adGroup, contract, activeTab, refetch }: AdGroupFormProps) {
  const formOpenedAt = useRef(Date.now());
  const { apiHost } = useConfig();
  const [action, setAction] = useState<string>();
  const { showSnackBar } = useSnackBar();
  const params = useParams();
  const navigate = useNavigate();
  const { constants } = useConstants();
  const contractConfigurationContext = useContractConfigurationContext();

  const isNewAdGroup = !adGroup?.id;

  const refetchContract = get(contractConfigurationContext, 'refetch');
  const setOnToggleAdGroupPauseCallback = get(contractConfigurationContext, 'setOnToggleAdGroupPauseCallback');
  const lineItemsData: any = get(contractConfigurationContext, 'lineItemsData');
  const mainBodyWidth = get(contractConfigurationContext, 'mainBodyWidth');

  useEffect(() => {
    if (setOnToggleAdGroupPauseCallback && refetch) {
      setOnToggleAdGroupPauseCallback(() => refetch);
    }
  }, [setOnToggleAdGroupPauseCallback, refetch]);

  const STAGE = get(constants, 'RAILS.STAGE');
  const API_HOST = apiHostMap[STAGE];

  const adGroupForm = useForm<z.infer<typeof AdGroupSchema>>({
    resolver: zodResolver(AdGroupSchema),
    defaultValues: adGroup,
  });

  const {
    watch,
    getValues,
    setValue,
    reset,
    formState: { isDirty },
  } = adGroupForm;

  useConfirmNavChange(() => isDirty);

  const [momentsType, conquest] = watch(['momentsType', 'conquest']);
  const isInHandMoments = momentsType === 'InHand';

  const TabValues = {
    GENERAL: 'General',
    TARGETING: 'Targeting',
    LOCATIONS: `${conquest ? 'Conquest ' : ''}Locations`,
    CREATIVES: isInHandMoments ? 'InHand Products' : 'Creatives',
  };

  const sectionRefs = useRef({});

  const { insertionOrderId: lineItemId } = adGroup;
  const { refetch: refetchAdGroups } = useAdGroups(lineItemId);

  const isPaused = getValues('paused');
  const togglePauseButtonText = isPaused ? 'Resume' : 'Pause';
  const togglePauseButtonColor: OverridableStringUnion<
    'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
    ButtonPropsColorOverrides
  > = isPaused ? 'warning' : 'primary';
  const togglePauseVariant = isPaused ? 'contained' : 'text';

  const pauseToggleMutation = usePauseToggleAdGroup(adGroup.id);

  const togglePause = () => {
    pauseToggleMutation.mutate(undefined, {
      onSuccess: (data) => {
        const newIsPaused = data?.model?.paused;
        setValue('paused', newIsPaused);
        refetchContract();
        refetchAdGroups();
        showSnackBar(`Ad group ${adGroup.name} was successfully ${newIsPaused ? 'paused' : 'resumed'}.`, 'success', {
          event: newIsPaused ? 'ad_group_paused' : 'ad_group_resumed',
          ad_group_id: adGroup.id,
          ad_group_name: adGroup.name,
        });
      },
      onError: () => {
        showSnackBar(`Ad group ${adGroup.name} failed to pause.`, 'error', {
          event: 'ad_group_pause_error',
          ad_group_id: adGroup.id,
          ad_group_name: adGroup.name,
        });
      },
    });
  };

  const adGroupMutation = useMutation({
    mutationFn: (data) => {
      const newAdGroup = mapFormToAdGroup(data);
      return saveAdGroup(apiHost, newAdGroup);
    },
    onSuccess: (data) => {
      if (!data.errors) {
        showSnackBar('Ad Group saved successfully!', 'success', {
          event: isNewAdGroup ? 'ad_group_created' : 'ad_group_updated',
          ad_group_id: data.model.id,
          ad_group_name: data.model.name,
          line_item_id: data.model.insertionOrderId,
          formOpenedAt: formOpenedAt.current,
        });
        refetchAdGroups();
        reset(mapAdGroupToForm(data.model));

        // Wait 1 sec so the form has time to reset the isDirty flag so it will not ask to confirm changes.
        switch (action) {
          case 'save':
            if (isNewAdGroup) {
              setTimeout(() => {
                navigate(generatePath(EDIT_AD_GROUP_URL, { id: params.id, adGroupId: data.model.id }));
              }, 1000);
            }
            break;
          case 'saveAndNext':
            setTimeout(() => {
              navigate(generatePath(ADMIN_CONTRACT_REVIEW_URL, { id: params.id }));
            }, 1000);
            break;
          default:
            break;
        }
      } else {
        showSnackBar(data.errors, 'error', { event: 'ad_group_save_error' });
      }
    },
    onError: () => {
      showSnackBar('Something wrong happened!', 'error', { event: 'ad_group_save_error' });
    },
  });

  const handleSubmit = async (data: any, event?: React.BaseSyntheticEvent) => {
    setAction(((event?.nativeEvent as SubmitEvent)?.submitter as any)?.name);
    adGroupMutation.mutate(data);
  };

  // disabling the eslint warning here since saveAdGroup is always a new object
  // which triggers re-render loop
  React.useEffect(() => {
    reset(adGroup);
    adGroupMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adGroupForm, adGroup, reset]);

  // The form state errors are not user-friendly. Best we can do is output to console for debugging.
  if (!isEmpty(adGroupForm.formState?.errors)) {
    // eslint-disable-next-line no-console
    console.log(adGroupForm.formState?.errors);
  }

  const [showModal, hideModal] = useModal(() => <DuplicateAdGroupForm adGroup={adGroup} onClose={hideModal} />, null, [
    adGroup,
  ]);
  const { openVersionsModal } = useVersions(ModelType.AdGroup, adGroup.id);

  const lineItem = lineItemsData?.find((lineItemItem) => lineItemItem.id === adGroup.insertionOrderId);

  const [delay, setDelay] = useState(true);
  const previousAdGroupId = usePrevious(adGroup.id);
  useEffect(() => {
    if (adGroup.id !== previousAdGroupId) {
      // Reset the form delay when adGroup changes
      setDelay(true);
      setTimeout(() => setDelay(false), 1000);
    }
  }, [adGroup.id, previousAdGroupId]);

  const pageTitle = [
    <Link
      href={generatePath(EDIT_LINE_ITEM_URL, {
        id: adGroup.contractId.toString(),
        lineItemId: lineItem?.id.toString() || '',
      })}
      color="#616161"
    >
      {lineItem?.name}
    </Link>,
    <>
      <span>{adGroup.id ? adGroup.name : 'New Ad Group'}</span>
      {delay && <CircularProgress size={12} sx={{ ml: 1 }} />}
    </>,
  ];

  const statusPageUrl = `${API_HOST}${generatePath(API_AD_GROUP_STATUS_URL, {
    adGroupId: adGroup?.id?.toString() || '',
  })}`;

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 1,
          left: 0,
          zIndex: 10,
          backgroundColor: '#F9F9F9',
        }}
      >
        <ContractConfigurationBreadcrumb title={pageTitle} />
        <Stack px={2} direction="row" justifyContent="space-between">
          <Stack spacing={2} direction="row" alignItems="center">
            <Typography variant="h5">{adGroup.id ? 'Edit' : 'Create New'} Ad Group</Typography>
            <Button disabled={!adGroup.id} variant="text" onClick={openVersionsModal}>
              See Changelog
            </Button>
            <Button
              variant={togglePauseVariant}
              color={togglePauseButtonColor}
              disabled={!adGroup.id}
              onClick={togglePause}
            >
              {togglePauseButtonText}
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <ContractReportsPageLink contractId={contract.id} />
            <LookerLink adGroupId={adGroup?.id} />
          </Stack>
        </Stack>
        <AdGroupTabs tabValues={TabValues} sectionRefs={sectionRefs} activeTab={activeTab} />
      </Stack>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...adGroupForm}>
        <form onSubmit={adGroupForm.handleSubmit(handleSubmit)}>
          <Stack spacing={2} p={2} pb={10}>
            {adGroup.id && <CampaignReadinessPanel campaignId={adGroup.id} />}
            <AdGroupGeneralSection
              bidSourceType={contract?.bid_source_type}
              ref={(el) => {
                // eslint-disable-next-line @typescript-eslint/dot-notation
                sectionRefs.current['GENERAL'] = el;
              }}
            />
            <AdGroupTargetingSection
              ref={(el) => {
                // eslint-disable-next-line @typescript-eslint/dot-notation
                sectionRefs.current['TARGETING'] = el;
              }}
            />
            <AdGroupLocationsSection
              ref={(el) => {
                // eslint-disable-next-line @typescript-eslint/dot-notation
                sectionRefs.current['LOCATIONS'] = el;
              }}
            />
            {!isInHandMoments && (
              <AdGroupCreativesSection
                advertiserId={contract?.advertiser_id}
                lineItemId={lineItem?.id}
                ref={(el) => {
                  // eslint-disable-next-line @typescript-eslint/dot-notation
                  sectionRefs.current['CREATIVES'] = el;
                }}
              />
            )}
            {isInHandMoments && (
              <AdGroupProductsSection
                advertiserId={contract.advertiser_id}
                ref={(el) => {
                  // eslint-disable-next-line @typescript-eslint/dot-notation
                  sectionRefs.current['CREATIVES'] = el;
                }}
              />
            )}
          </Stack>
          <FormEndOptions position="fixed" width={`${mainBodyWidth}px`}>
            {adGroup.id && (
              <>
                <Button variant="outlined" name="clone" disabled={adGroupMutation.isLoading} onClick={showModal}>
                  Duplicate
                </Button>
                <Button
                  variant="outlined"
                  name="status"
                  disabled={adGroupMutation.isLoading}
                  onClick={() => window.open(statusPageUrl, '_blank')}
                >
                  Status
                </Button>
              </>
            )}
            <Button type="submit" name="save" disabled={adGroupMutation.isLoading || !isDirty}>
              Save Ad Group
            </Button>
            {/* disabling this for now until we have the next page available */}
            <Button type="submit" name="saveAndNext" disabled>
              Save & Next
            </Button>
          </FormEndOptions>
        </form>
      </FormProvider>
    </>
  );
}

export default AdGroupForm;
